import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { configure as hotkeysConfigure } from 'react-hotkeys'
import { QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Provider } from 'react-redux'
import { Redirect, Route, Router, Switch } from 'react-router-dom'
import AppAuthWrapper from 'screens/AppAuthWrapper'
import LocationProvider from 'components/LocationProvider'
import TrackingPixels from 'components/TrackingPixels/TrackingPixels'
import { REACT_APP_DISABLE_TRACKING, REACT_APP_READER_URL } from 'env'
import {
  ABOUT_ASYNC_REVIEW_PATH,
  ABOUT_PATH,
  ADMIN_PATH,
  ALL_FILES_PATH,
  ANALYTICS_PATH,
  ARTICLE_CANCEL_AND_MERGE_REVIEW_PATH,
  ARTICLE_CANCEL_REVIEW_PATH,
  ARTICLE_CLOSE_REVIEW_PATH,
  ARTICLE_DEBUG_PATH,
  ARTICLE_DISMISS_REVIEW_PATH,
  ARTICLE_ENDED_REVIEW_PATH,
  ARTICLE_REVIEW_PATH,
  ARTICLE_SUBMIT_REVIEW_PATH,
  ASANA_OAUTH_REDIRECT,
  ASYNC_REVIEW_PATH,
  AUTH_REDIRECT_PATH,
  BILLING_PATH,
  BLOG_CATEGORIES_PATH,
  BLOG_PATH,
  BLOG_POSTS_PATH,
  BRAND_KITS_PATH,
  CONTENT_PREFERENCES_PATH,
  CAREERS_PATH,
  CHANGELOG_PATH,
  CHECKOUT_SESSION_PATH,
  COMPANY_DATA_PATH,
  COMPANY_HANDBOOK_PATH,
  COMPARE_CODA_PATH,
  COMPARE_CONFLUENCE_PATH,
  COMPARE_GOOGLE_DOCS_PATH,
  COMPARE_MICROSOFT_WORD_PATH,
  COMPARE_NOTION_PATH,
  CONTACT_SALES_PATH,
  CREATE_ACCOUNT_PATH,
  CREATE_WORKSPACE_PATH,
  CUSTOMERS_PATH,
  DEMO_PATH,
  DEMO_SCHEDULED_PATH,
  DOC_ACTIVITY_PATH,
  DOC_COMPARE_PATH,
  DOC_HISTORY_PATH,
  DOC_INSIGHTS_PATH,
  DOC_PATH,
  EDITOR_SANDBOX_PATH,
  EXPORT_PATH,
  EXTERNAL_TEMPLATES_PATH,
  FACEBOOK_PATH,
  FEATURES_PATH,
  FLIPPER_PATH,
  FOLDER_DOC_INSIGHTS_PATH,
  FOLDER_PATH,
  FOLDER_TEMPLATES_PATH,
  FORGOT_PASSWORD_PATH,
  FORM_PATH,
  FOR_COMPLIANCE_MANAGEMENT_PATH,
  FOR_CONTRACT_MANAGEMENT_PATH,
  FOR_DOCUMENTATION_PATH,
  FOR_ENGINEERING_TEAMS_PATH,
  FOR_ENTERPRISE_PATH,
  FOR_INTERNAL_COLLABORATION_PATH,
  FOR_PUBLIC_DOCUMENTATION_PATH,
  FOR_STARTUPS_PATH,
  GITHUB_APP_REDIRECT_PATH,
  GOOGLE_PATH,
  APPLE_PATH,
  APPLE_OAUTH_CALLBACK_PATH,
  GROUPS_PATH,
  GROUP_PATH,
  HANDBOOKS_PATH,
  HANDBOOK_PATH,
  HIGHLIGHT_PREVIEW_PATH,
  HOME_ALTERNATIVE_PATH,
  HOME_PATH,
  INSTAGRAM_PLANNING_PATH,
  INTEGRATIONS_PATH,
  INVESTORS_PATH,
  JIRA_OAUTH_REDIRECT_PATH,
  KICKOFF_PATH,
  LOGIN_OR_SIGNUP_PATH,
  LOGIN_PATH,
  MAGAZINE_CATEGORIES_PATH,
  MAGAZINE_PATH,
  MAGAZINE_POSTS_PATH,
  MAINTENANCE_PATH,
  MEMBERS_PATH,
  MERGE_LAYER_PATH,
  MERGE_PATH,
  MWM_PATH,
  NEW_WORKSPACE_PATH,
  PASSWORD_RESET_PATH,
  PAYMENT_STATUS_PATH,
  PDF_DOC_PATH,
  PRESS_PATH,
  PRICING_PATH,
  PRIVACY_POLICY_PATH,
  PRODUCT_PATH,
  QUICK_IMPORT_PATH,
  RECENT_DOCS_PATH,
  REMOTE_TEAMS_PATH,
  RESERVE_PATH,
  SAML_PATH,
  SCHEDULE_DEMO_PATH,
  SCHEDULE_PATH,
  SELECT_ROLE_PATH,
  SHARED_WITH_ME_DOCS_PATH,
  SIDEKIQ_PATH,
  SIGNUP_PATH,
  SLACK_OAUTH_REDIRECT,
  START_FREE_TRIAL_PATH,
  SUGGESTION_MANUAL_MERGE,
  SUPPORTERS_PATH,
  TERMS_PATH,
  THANK_YOU_PATH,
  TODOS_PATH,
  WAITING_LIST_PATH,
  WIDGET_GOOGLE_OAUTH_PATH,
  WORKSPACE_GROUPS_PATH,
  WORKSPACE_JOIN_REQUEST_PATH,
  WORKSPACE_PATH,
  WORKSPACE_SELECTION_PATH,
  WORKSPACE_URL_JOIN,
  WRITE_PATH,
  X_OAUTH_REDIRECT_PATH,
  LINKEDIN_OAUTH_REDIRECT_PATH,
  DESIGN_PATH,
  FACEBOOK_OAUTH_REDIRECT_PATH,
  INSTAGRAM_OAUTH_REDIRECT_PATH,
  BRAND_KIT_PATH,
  BRAND_PLAN_PATH,
  SCHEDULED_POSTS_PATH,
  ACADEMY_PATH,
  BLAZE_HOME_PATH,
  MEDIA_FILE_PATH,
  OAUTH_V2_REDIRECT_PATH,
  FAVORITES_PATH,
  UPLOADS_PATH,
  UPLOADS_VIEWS_PATH,
  TEMPLATE_BINDERS_PATH,
  FOLDER_DOC_PATH,
  REFERRALS_PATH,
  TEMPLATES_PATH,
  PAYWALL_SUBSCRIBE_PATH,
  PAYWALL_START_FREE_TRIAL_PATH,
  PREVIEW_PATH,
  WIZARD_PATH,
  REGISTRATION_PATH,
  NATIVE_WEBVIEW_BRAND_KIT_PATH,
  VIRTUAL_MARKETER_ONBOARDING_PATH,
  VIRTUAL_MARKETER_ONBOARDING_TEST_PATH,
  VIRTUAL_MARKETER_PLAYGROUND_PATH,
  BRAND_PLAN_SCHEDULE_PATH,
  PAID_ADS_PATH,
  AUTOPILOT_REGISTRATION_PATH,
  SELECT_WORKSPACE_PATH,
  PAID_ADS_WIZARD_PATH,
  CAMPAIGN_PATH,
  CAMPAIGNS_PATH,
  BRAIN_PLAYGROUND_PATH,
  VISUAL_STYLES_PLAYGROUND_PATH,
  AUTOPILOT_INTEGRATIONS_PATH,
  APPROVALS_PATH, CONTENT_PLAN_PATH, CONTENT_PLAN_SCHEDULE_PATH,
} from 'paths'
import { AlgoliaClientWrapper } from 'providers/AlgoliaClientProvider'
import ProseInstanceProvider from 'providers/ProseInstanceProvider'
import store from 'redux/store'
import { AuthRedirect } from 'screens/AuthRedirect'
import { FaviconSetter } from 'components/FaviconSetter/FaviconSetter'
import LoginOrSignup from 'screens/LoginOrSignup/LoginOrSignup'
import { Paywall } from 'screens/Paywall/Paywall'
import { OnboardingLoaderContainer } from 'screens/LoginOrSignup/components/OnboardingLoader/OnboardingLoaderContainer'
import { lazyRetry } from 'utils/lazyRetry'
import './globalWindowPatches'
import './scrollIntoViewIfNeeded'

import { Intercom } from 'components/Intercom/Intercom'
import 'styles'

import { OverlayContainer } from '@react-aria/overlays'
import { BottomButtonsContainer as BottomButtons } from 'components/DocRightRailV2/BottomButtons/BottomButtons'
import { LazyComponentWrapper } from 'components/LazyComponentWrapper/LazyComponentWrapper'
import { LazyWrapper } from 'components/LazyWrapper/LazyWrapper'
import MaintenanceMode from 'components/MaintenanceMode/MaintenanceMode'
import { MaintenanceModeWrapper } from 'components/MaintenanceMode/MaintenanceModeWrapper'
import { URLModal } from 'components/ModalDialog/URLModal'
import { SearchBarPortalProvider } from 'components/SearchBarPortal/SearchBarPortalContext'
import { SentryAdditionalInfo } from 'components/SentryAdditionalInfo'
import { SentryRoute } from 'components/SentryRoute'
import { ThemeProvider } from 'components/ThemeProvider/ThemeProvider'
import WorkspaceDashboard from 'components/WorkspaceDashboard/WorkspaceDashboard'
import { useCustomHistory } from 'hooks/useHistory'
import { usePwaGlobalLinkHandler } from 'hooks/usePwaGlobalLinkHandler'
import { GtmAuthTracker } from 'providers/GtmAuthTracker'
import { WebsocketProvider } from 'providers/WebsocketProvider'
import ArticleIdWrapper from 'screens/ArticleViewWrapper/ArticleIdWrapper'
import QuickRedirect from 'screens/QuickRedirect/QuickRedirect'
import {
  useHandleMessageToTab,
  usePwaHistoryUpdater,
  usePwaKeyboardShortcutHandler,
  usePwaTitleUpdater,
} from 'screens/TabRoot/utils'
import WebflowHostedPage from 'screens/WebflowHostedPage'
import { RootStoreProvider } from 'stores/RootStore'
import { navigateToExternalUrl } from 'utils/location'
import { showReactQueryDevTools } from 'utils/showReactQueryDevTools'
import GoogleOauth from 'widgets/GoogleOauth/GoogleOauth'
import Styles from './App.module.scss'
import { ModalStack } from './almanac-ui/Modal'
import { AbilityProvider } from './components/Can'
import { GlobalModal } from './components/GlobalModal/GlobalModal'
import { ArticleViewProvider } from './providers/ArticleViewProvider/ArticleViewProvider'
import queryClient from './queryClient'
import { HandbookSpinner } from 'components/Handbook2_0/HandbookSpinner'
import { ConfettiWrapper } from 'components/ConfettiWrapper/ConfettiWrapper'
import { FeatureFlagAttributes } from 'components/FeatureFlagAttributes/FeatureFlagAttributes'
import { Viewport } from 'components/Layout/Viewport'
import FacebookLanding from 'screens/Landing/FacebookLanding'
import { DrawerWrapper } from 'components/Drawer/DrawerWrapper'
import { Onboarding } from 'components/Onboarding/Onboarding'
import { UpdatesProvider } from 'providers/UpdatesProvider/UpdatesProvider'
import { CloudinaryProvider } from 'providers/CloudinaryProvider/CloudinaryProvider'
import { TieredUsageListener } from 'components/TieredUsageListener/TieredUsageListener'
import { CreditUsageListener } from 'components/CreditUsageListener/CreditUsageListener'
import { FreemiumStartedListener } from 'components/FreemiumStartedListener/FreemiumStartedListener'
import { GoogleFontsLoader } from 'components/GoogleFontsLoader/GoogleFontsLoader'
import { FeatureFlagLoader } from 'components/FeatureFlagsLoader/FeatureFlagLoader'
import { AuthChannelProvider } from 'providers/AuthChannelProvider/AuthChannelProvider'
import { WIZARD_TOUR_PORTAL_ID } from 'components/Wizards/Wizard/WizardTour/Types'
import { VirtualMarketerOnboardingWrapper } from 'VirtualMarketerOnboardingWrapper/VirtualMarketerOnboardingWrapper'
import { BlazeFontsLoader } from 'components/BlazeFontsLoader/BlazeFontsLoader'
import { AutoPilotProvider } from 'components/AutoPilotProvider/AutoPilotProvider'
import { DatadogUserInfo } from 'components/DatadogUserInfo'

hotkeysConfigure({
  ignoreEventsCondition: (event) => {
    const target = event.target as HTMLElement | undefined

    if (target && target.tagName) {
      const tagName = target.tagName.toLowerCase()
      return ['input', 'select', 'textarea'].includes(tagName)
    }

    return false
  },
})

const AboutUs = lazyRetry(() => import('screens/AboutUs/AboutUs'))
const Billing = lazyRetry(() => import('screens/Billing/Billing'))
const ArticleDebug = lazyRetry(() => import('screens/ArticleDebug/ArticleDebug'))
const CompareContainer = lazyRetry(() => import('screens/Compare/CompareContainer'))

const CustomPathWrapper = lazyRetry(() => import('components/CustomPathWrapper/CustomPathWrapper'))
const Design = lazyRetry(() => import('components/Design/DesignRoute'))
const MediaFile = lazyRetry(() => import('components/MediaFile/MediaFileRoute'))
const EditorDemo = lazyRetry(() => import('screens/EditorDemo/EditorDemo'))
const Export = lazyRetry(() => import('screens/Export/Export'))
const Preview = lazyRetry(() => import('screens/Preview/Preview'))
const ForgotPassword = lazyRetry(() => import('screens/NewLogin/ForgotPassword'))
const GitHubAppRedirect = lazyRetry(() => import('screens/GitHubAppRedirect/GitHubAppRedirect'))
const GoogleLanding = lazyRetry(() => import('screens/Landing/GoogleLanding'))
const AppleLanding = lazyRetry(() => import('screens/Landing/AppleLanding'))
const AppleOauthRedirect = lazyRetry(() => import('screens/AppleOauthRedirect/AppleOauthRedirect'))
const Handbooks = lazyRetry(() => import('screens/Handbooks/Handbooks'))
const HandbookWrapper = lazyRetry(() => import('components/Handbook2_0/HandbookWrapper'))
const HighlightPreview = lazyRetry(() => import('screens/HighlightPreview/HighlightPreview'))
const HistoryContainer = lazyRetry(() => import('screens/History/HistoryContainer'))
const HomeLanding = lazyRetry(() => import('screens/HomeLanding/HomeLanding'))
const JiraOauthRedirect = lazyRetry(() => import('screens/JiraOauthRedirect/JiraOauthRedirect'))
const Members = lazyRetry(() => import('screens/Members/Members'))
const Merge = lazyRetry(() => import('screens/Merge/MergeContainer'))
const OurSupporters = lazyRetry(() => import('screens/OurSupporters/OurSupporters'))
const PdfDoc = lazyRetry(() => import('screens/PdfDoc/PdfDoc'))
const ProductLanding = lazyRetry(() => import('screens/ProductLanding/ProductLanding'))
const RecentDocsContainer = lazyRetry(() => import('screens/RecentDocsContainer/RecentDocsContainer'))
const ResetPassword = lazyRetry(() => import('screens/NewLogin/ResetPassword/ResetPassword'))
const SamlLanding = lazyRetry(() => import('screens/Landing/SamlLanding'))
const SelectAutopilotWorkspace = lazyRetry(() => import('screens/SelectAutopilotWorkspace/SelectAutopilotWorkspace'))
const CreateAutopilotWorkspace = lazyRetry(() => import('screens/CreateAutopilotWorkspace/CreateAutopilotWorkspace'))
const SharedWithMeDocsContainer = lazyRetry(() => import('screens/SharedWithMeDocsContainer/SharedWithMeDocsContainer'))
const SlackOauthLanding = lazyRetry(() => import('screens/SlackOauthLanding/SlackOauthLanding'))
const AsanaOauthLanding = lazyRetry(() => import('screens/AsanaOauthLanding/AsanaOauthLanding'))
const SuggestionManualMerge = lazyRetry(() => import('screens/SuggestionManualMerge/MergeContainer'))
const TeamContainer = lazyRetry(() => import('screens/Team/TeamContainer'))
const TeamsContainer = lazyRetry(() => import('screens/Teams/TeamsContainer'))
const Todos = lazyRetry(() => import('screens/Todos/Todos'))
const BrandKits = lazyRetry(() => import('screens/BrandKits/BrandKits'))
const ContentPreferencesScreen = lazyRetry(() => import('screens/ContentPreferences/ContentPreferencesScreen'))
const BrandKit = lazyRetry(() => import('screens/BrandKit/BrandKit'))
const VirtualMarketerOnboarding = lazyRetry(() => import('screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding'))
const VirtualMarketerOnboardingTest = lazyRetry(
  () => import('screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding'),
)
const VirtualMarketerPlayground = lazyRetry(() => import('screens/VirtualMarketerPlayground/VirtualMarketerPlayground'))
const BrainPlayground = lazyRetry(() => import('screens/BrainPlayground/BrainPlayground'))
const VisualStylesPlayground = lazyRetry(() => import('screens/VisualStylesPlayground/VisualStylesPlayground'))
const WorkspaceGroups = lazyRetry(() => import('screens/WorkspaceGroups/WorkspaceGroups'))
const WorkspaceJoinRequest = lazyRetry(() => import('screens/WorkspaceJoinRequest/WorkspaceJoinRequest'))
const WorkspaceUrlJoin = lazyRetry(() => import('screens/WorkspaceUrlJoin/WorkspaceUrlJoin'))
const Write = lazyRetry(() => import('screens/Write/Write'))
const Referrals = lazyRetry(() => import('screens/Referrals/Referrals'))
const MergeLayer = lazyRetry(() => import('screens/MergeLayer/MergeLayerContainer'))
const PaymentStatus = lazyRetry(() => import('screens/PaymentStatus/PaymentStatus'))
const CheckoutSession = lazyRetry(() => import('screens/CheckoutSession/CheckoutSession'))
const ScheduledPosts = lazyRetry(() => import('screens/ScheduledPosts/ScheduledPosts'))
const Academy = lazyRetry(() => import('screens/Academy/Academy'))
const Uploads = lazyRetry(() => import('screens/Uploads/Uploads'))
const InstagramPlanning = lazyRetry(() => import('screens/InstagramPlanning/InstagramPlanning'))
const TemplateBinders = lazyRetry(() => import('components/TemplateBinders/TemplateBindersRoute'))
const ExternalTemplates = lazyRetry(() => import('screens/Templates/External/ExternalTemplates'))
const Templates = lazyRetry(() => import('screens/Templates/TemplatesRoute'))
const Signup = lazyRetry(() => import('screens/Signup/Signup'))
const Registration = lazyRetry(() => import('screens/Registration/RegistrationAndOnboarding'))
const AutopilotRegistration = lazyRetry(
  () => import('screens/AutopilotRegistration/AutopilotRegistrationAndOnboarding'),
)
const ContentPlan = lazyRetry(() => import('screens/ContentPlan/ContentPlan'))
const ContentPlanSchedule = lazyRetry(() => import('screens/Schedule/Schedule'))
const Integrations = lazyRetry(() => import('screens/Integrations/Integrations'))
const ActivityContainer = lazyRetry(() => import('./screens/Activity/ActivityContainer'))
const XOauthRedirect = lazyRetry(() => import('screens/XOauthRedirect/XOauthRedirect'))
const LinkedinOauthRedirect = lazyRetry(() => import('screens/LinkedinOauthRedirect/LinkedinOauthRedirect'))
const FacebookOauthRedirect = lazyRetry(() => import('screens/FacebookOauthRedirect/FacebookOauthRedirect'))
const InstagramOauthRedirect = lazyRetry(() => import('screens/InstagramOauthRedirect/InstagramOauthRedirect'))
const OAuthRedirectV2 = lazyRetry(() => import('screens/OAuthRedirectV2/OAuthRedirectV2'))
const AllFiles = lazyRetry(() => import('screens/AllFiles/AllFiles'))
const Favorites = lazyRetry(() => import('screens/Favorites/Favorites'))
const Analytics = lazyRetry(() => import('screens/Analytics/Analytics'))
const PaidAds = lazyRetry(() => import('screens/PaidAds/PaidAds'))
const Home = lazyRetry(() => import('screens/blaze/Home/Home').then(({ Home }) => ({ default: Home })))
const WizardScreen = lazyRetry(() =>
  import('screens/blaze/Wizards/WizardScreen').then(({ WizardScreen }) => ({ default: WizardScreen })),
)
const BrandKitsScreen = lazyRetry(() =>
  import('screens/BrandKits/BrandKitsScreen').then(({ BrandKitsScreen }) => ({ default: BrandKitsScreen })),
)
const Approvals = lazyRetry(() => import('screens/Approvals/Approvals'))

const marketingComparePaths = [
  COMPARE_CODA_PATH,
  COMPARE_CONFLUENCE_PATH,
  COMPARE_GOOGLE_DOCS_PATH,
  COMPARE_MICROSOFT_WORD_PATH,
  COMPARE_NOTION_PATH,
]

const marketingForPaths = [
  FOR_COMPLIANCE_MANAGEMENT_PATH,
  FOR_CONTRACT_MANAGEMENT_PATH,
  FOR_DOCUMENTATION_PATH,
  FOR_ENGINEERING_TEAMS_PATH,
  FOR_ENTERPRISE_PATH,
  FOR_INTERNAL_COLLABORATION_PATH,
  FOR_PUBLIC_DOCUMENTATION_PATH,
  FOR_STARTUPS_PATH,
]

const App = () => {
  const history = useCustomHistory()
  usePwaGlobalLinkHandler()
  usePwaKeyboardShortcutHandler()
  usePwaTitleUpdater()
  useHandleMessageToTab()
  usePwaHistoryUpdater(history)

  useEffect(() => {
    // Load Stripe
    if (!document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.async = true
      document.head.appendChild(script)
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <OverlayContainer className={Styles.overlayContainer}>
        <RootStoreProvider>
          <Provider store={store}>
            <CloudinaryProvider>
              <FaviconSetter />
              <GoogleFontsLoader />
              <BlazeFontsLoader />
              <Router history={history}>
                <AuthChannelProvider>
                  <WebsocketProvider>
                    <ProseInstanceProvider>
                      <ModalStack>
                        <UpdatesProvider>
                          <Route exact path={MAINTENANCE_PATH} component={MaintenanceMode} />
                          <GtmAuthTracker />
                          <Switch>
                            <SentryRoute
                              exact
                              path={HIGHLIGHT_PREVIEW_PATH}
                              component={LazyWrapper(HighlightPreview)}
                            />
                            <SentryRoute exact path={EXPORT_PATH} component={LazyWrapper(Export)} />
                            <SentryRoute exact path={PREVIEW_PATH} component={LazyWrapper(Preview)} />
                            <SentryRoute exact path={AUTH_REDIRECT_PATH} component={AuthRedirect} />
                            <SentryRoute exact path={QUICK_IMPORT_PATH} component={LazyWrapper(QuickRedirect)} />
                            <TrackingPixels>
                              <SentryAdditionalInfo>
                                <LocationProvider>
                                  <AbilityProvider>
                                    <AlgoliaClientWrapper>
                                      <ThemeProvider />
                                      <TieredUsageListener />
                                      <DatadogUserInfo />
                                      <AutoPilotProvider />
                                      <Switch>
                                        <SentryRoute exact path={[DOC_PATH, FOLDER_DOC_PATH, DESIGN_PATH]} />
                                        <SentryRoute component={DrawerWrapper} />
                                      </Switch>
                                      {REACT_APP_DISABLE_TRACKING || <Intercom />}
                                      <OnboardingLoaderContainer />
                                      <Switch>
                                        <SentryRoute
                                          exact
                                          path={[
                                            OAUTH_V2_REDIRECT_PATH,
                                            FACEBOOK_OAUTH_REDIRECT_PATH,
                                            INSTAGRAM_OAUTH_REDIRECT_PATH,
                                          ]}
                                        />
                                        <SentryRoute component={Onboarding} />
                                      </Switch>
                                      <ConfettiWrapper />
                                      <Switch>
                                        <SentryRoute
                                          exact
                                          path={ADMIN_PATH}
                                          component={() => {
                                            if (process.env.REACT_APP_ENV === 'dev') {
                                              navigateToExternalUrl(`${REACT_APP_READER_URL}/admin`)
                                            }
                                            return null
                                          }}
                                        />
                                        <SentryRoute
                                          exact
                                          path={FLIPPER_PATH}
                                          component={() => {
                                            if (process.env.REACT_APP_ENV === 'dev') {
                                              navigateToExternalUrl(`${REACT_APP_READER_URL}/flipper`)
                                            }
                                            return null
                                          }}
                                        />
                                        <SentryRoute
                                          exact
                                          path={SIDEKIQ_PATH}
                                          component={() => {
                                            if (process.env.REACT_APP_ENV === 'dev') {
                                              navigateToExternalUrl(`${REACT_APP_READER_URL}/sidekiq`)
                                            }
                                            return null
                                          }}
                                        />
                                        <SentryRoute exact path={WIDGET_GOOGLE_OAUTH_PATH} component={GoogleOauth} />
                                        <SentryRoute exact path={SAML_PATH} component={LazyWrapper(SamlLanding)} />
                                        <SentryRoute
                                          exact
                                          path={PAYMENT_STATUS_PATH}
                                          component={LazyWrapper(PaymentStatus)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CHECKOUT_SESSION_PATH}
                                          component={LazyWrapper(CheckoutSession)}
                                        />
                                        <SentryRoute exact path={GOOGLE_PATH} component={LazyWrapper(GoogleLanding)} />
                                        <SentryRoute
                                          exact
                                          path={APPLE_PATH}
                                          component={LazyWrapper(AppleOauthRedirect)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={APPLE_OAUTH_CALLBACK_PATH}
                                          component={LazyWrapper(AppleLanding)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={FACEBOOK_PATH}
                                          component={LazyWrapper(FacebookLanding)}
                                        />
                                        <SentryRoute exact path={WORKSPACE_PATH} component={LazyWrapper(HomeLanding)} />
                                        <SentryRoute exact path={HOME_PATH} component={LazyWrapper(HomeLanding)} />
                                        <SentryRoute exact path={LOGIN_PATH} component={LazyWrapper(LoginOrSignup)} />
                                        <SentryRoute exact path={SIGNUP_PATH} component={LazyWrapper(Signup)} />
                                        <SentryRoute
                                          exact
                                          path={REGISTRATION_PATH}
                                          component={LazyWrapper(Registration)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={AUTOPILOT_REGISTRATION_PATH}
                                          component={LazyWrapper(AutopilotRegistration)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={INSTAGRAM_PLANNING_PATH}
                                          component={LazyWrapper(InstagramPlanning)}
                                        />
                                        <SentryRoute exact path={BILLING_PATH} component={LazyWrapper(Billing)} />
                                        <SentryRoute
                                          exact
                                          path={EXTERNAL_TEMPLATES_PATH}
                                          component={LazyWrapper(ExternalTemplates)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={LOGIN_OR_SIGNUP_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={OAUTH_V2_REDIRECT_PATH}
                                          component={LazyWrapper(OAuthRedirectV2)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={X_OAUTH_REDIRECT_PATH}
                                          component={LazyWrapper(XOauthRedirect)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={LINKEDIN_OAUTH_REDIRECT_PATH}
                                          component={LazyWrapper(LinkedinOauthRedirect)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={FACEBOOK_OAUTH_REDIRECT_PATH}
                                          component={LazyWrapper(FacebookOauthRedirect)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={INSTAGRAM_OAUTH_REDIRECT_PATH}
                                          component={LazyWrapper(InstagramOauthRedirect)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CREATE_WORKSPACE_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={SELECT_ROLE_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={COMPANY_DATA_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={START_FREE_TRIAL_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CREATE_ACCOUNT_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={SCHEDULE_DEMO_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={WAITING_LIST_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={DEMO_SCHEDULED_PATH}
                                          component={LazyWrapper(LoginOrSignup)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={PAYWALL_START_FREE_TRIAL_PATH}
                                          component={LazyWrapper(Paywall)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={PAYWALL_SUBSCRIBE_PATH}
                                          component={LazyWrapper(Paywall)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={VIRTUAL_MARKETER_ONBOARDING_PATH}
                                          component={LazyWrapper(VirtualMarketerOnboarding)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={VIRTUAL_MARKETER_ONBOARDING_TEST_PATH}
                                          component={LazyWrapper(VirtualMarketerOnboardingTest)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={FORGOT_PASSWORD_PATH}
                                          component={LazyWrapper(ForgotPassword)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={PASSWORD_RESET_PATH}
                                          component={LazyWrapper(ResetPassword)}
                                        />
                                        <SentryRoute exact path={ABOUT_PATH} component={LazyWrapper(AboutUs)} />
                                        <SentryRoute
                                          exact
                                          path={SUPPORTERS_PATH}
                                          component={LazyWrapper(OurSupporters)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={PRICING_PATH}
                                          component={LazyWrapper(ProductLanding)}
                                        />
                                        <SentryRoute exact path={BLOG_PATH} component={WebflowHostedPage(BLOG_PATH)} />
                                        <SentryRoute
                                          exact
                                          path={MAGAZINE_PATH}
                                          // Redirect /magazine to /blog before switching to external site
                                          component={WebflowHostedPage(BLOG_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={BLOG_POSTS_PATH}
                                          render={(routeProps) =>
                                            WebflowHostedPage(routeProps.location.pathname)(routeProps)
                                          }
                                        />
                                        <SentryRoute
                                          exact
                                          path={MAGAZINE_POSTS_PATH}
                                          render={(routeProps) =>
                                            // Redirect magazine posts path to blog categories before switching to external site
                                            WebflowHostedPage(
                                              routeProps.location.pathname.replace('/magazine-posts/', '/blog-posts/'),
                                            )(routeProps)
                                          }
                                        />
                                        <SentryRoute
                                          exact
                                          path={BLOG_CATEGORIES_PATH}
                                          render={(routeProps) =>
                                            WebflowHostedPage(routeProps.location.pathname)(routeProps)
                                          }
                                        />
                                        <SentryRoute
                                          exact
                                          path={MAGAZINE_CATEGORIES_PATH}
                                          render={(routeProps) =>
                                            // Redirect magazine categories path to blog categories before switching to external site
                                            WebflowHostedPage(
                                              routeProps.location.pathname.replace(
                                                '/magazine-categories/',
                                                '/blog-categories/',
                                              ),
                                            )(routeProps)
                                          }
                                        />
                                        <SentryRoute
                                          exact
                                          path={CAREERS_PATH}
                                          component={WebflowHostedPage(CAREERS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={HOME_ALTERNATIVE_PATH}
                                          component={WebflowHostedPage(HOME_ALTERNATIVE_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={ABOUT_ASYNC_REVIEW_PATH}
                                          component={WebflowHostedPage(ABOUT_ASYNC_REVIEW_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={ASYNC_REVIEW_PATH}
                                          component={WebflowHostedPage(ASYNC_REVIEW_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CHANGELOG_PATH}
                                          component={WebflowHostedPage(CHANGELOG_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={COMPANY_HANDBOOK_PATH}
                                          component={WebflowHostedPage(COMPANY_HANDBOOK_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CONTACT_SALES_PATH}
                                          component={WebflowHostedPage(CONTACT_SALES_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={CUSTOMERS_PATH}
                                          component={WebflowHostedPage(CUSTOMERS_PATH)}
                                        />
                                        <SentryRoute exact path={DEMO_PATH} component={WebflowHostedPage(DEMO_PATH)} />
                                        <SentryRoute
                                          exact
                                          path={FEATURES_PATH}
                                          component={WebflowHostedPage(FEATURES_PATH)}
                                        />
                                        {marketingForPaths.map((forPath) => (
                                          <SentryRoute
                                            exact
                                            path={forPath}
                                            component={WebflowHostedPage(forPath)}
                                            key={forPath}
                                          />
                                        ))}
                                        {marketingComparePaths.map((comparePath) => (
                                          <SentryRoute
                                            exact
                                            path={comparePath}
                                            component={WebflowHostedPage(comparePath)}
                                            key={comparePath}
                                          />
                                        ))}
                                        <SentryRoute exact path={FORM_PATH} component={WebflowHostedPage(FORM_PATH)} />
                                        <SentryRoute
                                          exact
                                          path={INTEGRATIONS_PATH}
                                          component={WebflowHostedPage(INTEGRATIONS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={INVESTORS_PATH}
                                          component={WebflowHostedPage(INVESTORS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={KICKOFF_PATH}
                                          component={WebflowHostedPage(KICKOFF_PATH)}
                                        />
                                        <SentryRoute
                                          path={MWM_PATH}
                                          render={(routeProps) =>
                                            // Redirect magazine categories path to blog categories before switching to external site
                                            WebflowHostedPage(routeProps.location.pathname)(routeProps)
                                          }
                                        />
                                        <SentryRoute
                                          exact
                                          path={PRIVACY_POLICY_PATH}
                                          component={WebflowHostedPage(PRIVACY_POLICY_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={PRESS_PATH}
                                          component={WebflowHostedPage(PRESS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={RESERVE_PATH}
                                          component={WebflowHostedPage(RESERVE_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={REMOTE_TEAMS_PATH}
                                          component={WebflowHostedPage(REMOTE_TEAMS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={SCHEDULE_PATH}
                                          component={WebflowHostedPage(SCHEDULE_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={TERMS_PATH}
                                          component={WebflowHostedPage(TERMS_PATH)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={THANK_YOU_PATH}
                                          component={WebflowHostedPage(THANK_YOU_PATH)}
                                        />
                                        <Redirect from={PRODUCT_PATH} to={HOME_PATH} />
                                        <SentryRoute exact path={PDF_DOC_PATH} component={LazyWrapper(PdfDoc)} />
                                        <SentryRoute
                                          exact
                                          path={SUGGESTION_MANUAL_MERGE}
                                          component={LazyWrapper(SuggestionManualMerge)}
                                        />
                                        <SentryRoute exact path={MERGE_PATH} component={LazyWrapper(Merge)} />
                                        <SentryRoute
                                          exact
                                          path={MERGE_LAYER_PATH}
                                          component={LazyWrapper(MergeLayer)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={DOC_COMPARE_PATH}
                                          component={LazyWrapper(CompareContainer)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={DOC_ACTIVITY_PATH}
                                          component={LazyWrapper(ActivityContainer)}
                                        />
                                        <SentryRoute
                                          exact
                                          path={DOC_HISTORY_PATH}
                                          component={LazyWrapper(HistoryContainer)}
                                        />
                                        <Switch>
                                          <SentryRoute
                                            exact
                                            path={WORKSPACE_SELECTION_PATH}
                                            component={LazyWrapper(SelectAutopilotWorkspace)}
                                          />
                                          <SentryRoute
                                            exact
                                            path={SELECT_WORKSPACE_PATH}
                                            component={LazyWrapper(SelectAutopilotWorkspace)}
                                          />
                                          <SentryRoute
                                            exact
                                            path={NEW_WORKSPACE_PATH}
                                            component={LazyWrapper(CreateAutopilotWorkspace)}
                                          />
                                          <SentryRoute
                                            exact
                                            path={SLACK_OAUTH_REDIRECT}
                                            component={LazyWrapper(SlackOauthLanding)}
                                          />
                                          <SentryRoute
                                            exact
                                            path={ASANA_OAUTH_REDIRECT}
                                            component={LazyWrapper(AsanaOauthLanding)}
                                          />
                                          <SentryRoute
                                            exact
                                            path={WORKSPACE_URL_JOIN}
                                            component={LazyWrapper(WorkspaceUrlJoin)}
                                          />
                                          <MaintenanceModeWrapper>
                                            <VirtualMarketerOnboardingWrapper>
                                              <ArticleViewProvider>
                                                <CreditUsageListener />
                                                <FreemiumStartedListener />
                                                <BottomButtons />
                                                <div id={WIZARD_TOUR_PORTAL_ID} />
                                                <SearchBarPortalProvider>
                                                  <WorkspaceDashboard>
                                                    <Switch>
                                                      <SentryRoute
                                                        exact
                                                        path={[
                                                          DOC_PATH,
                                                          DOC_INSIGHTS_PATH,
                                                          ARTICLE_REVIEW_PATH,
                                                          ARTICLE_SUBMIT_REVIEW_PATH,
                                                          ARTICLE_DISMISS_REVIEW_PATH,
                                                          ARTICLE_CANCEL_REVIEW_PATH,
                                                          ARTICLE_CLOSE_REVIEW_PATH,
                                                          ARTICLE_CANCEL_AND_MERGE_REVIEW_PATH,
                                                          ARTICLE_ENDED_REVIEW_PATH,
                                                        ]}
                                                        component={ArticleIdWrapper}
                                                      />
                                                      <SentryRoute
                                                        exact
                                                        path={TEMPLATE_BINDERS_PATH}
                                                        component={LazyWrapper(TemplateBinders)}
                                                      />
                                                      <SentryRoute
                                                        exact
                                                        path={REFERRALS_PATH}
                                                        component={LazyWrapper(Referrals)}
                                                      />
                                                      <SentryRoute
                                                        exact
                                                        path={TEMPLATES_PATH}
                                                        component={LazyWrapper(Templates)}
                                                      />
                                                      <SentryRoute
                                                        exact
                                                        path={MEDIA_FILE_PATH}
                                                        component={LazyWrapper(MediaFile)}
                                                      />
                                                      <SentryRoute
                                                        exact
                                                        path={[
                                                          HANDBOOK_PATH,
                                                          FOLDER_PATH,
                                                          FOLDER_TEMPLATES_PATH,
                                                          FOLDER_DOC_INSIGHTS_PATH,
                                                        ]}
                                                        render={() => (
                                                          <LazyComponentWrapper
                                                            overrideFallback={<HandbookSpinner />}
                                                            component={<HandbookWrapper />}
                                                          />
                                                        )}
                                                      />
                                                      <AppAuthWrapper>
                                                        <Switch>
                                                          <SentryRoute
                                                            exact
                                                            path={GITHUB_APP_REDIRECT_PATH}
                                                            component={LazyWrapper(GitHubAppRedirect)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={JIRA_OAUTH_REDIRECT_PATH}
                                                            component={LazyWrapper(JiraOauthRedirect)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={WORKSPACE_GROUPS_PATH}
                                                            component={LazyWrapper(WorkspaceGroups)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={GROUP_PATH}
                                                            component={LazyWrapper(TeamContainer)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={GROUPS_PATH}
                                                            component={LazyWrapper(TeamsContainer)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={RECENT_DOCS_PATH}
                                                            component={LazyWrapper(RecentDocsContainer)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={SHARED_WITH_ME_DOCS_PATH}
                                                            component={LazyWrapper(SharedWithMeDocsContainer)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={ALL_FILES_PATH}
                                                            component={LazyWrapper(AllFiles)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={FAVORITES_PATH}
                                                            component={LazyWrapper(Favorites)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={TODOS_PATH}
                                                            component={LazyWrapper(Todos)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={SCHEDULED_POSTS_PATH}
                                                            component={LazyWrapper(ScheduledPosts)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={[PAID_ADS_WIZARD_PATH, PAID_ADS_PATH]}
                                                            component={LazyWrapper(PaidAds)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={ANALYTICS_PATH}
                                                            component={LazyWrapper(Analytics)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={AUTOPILOT_INTEGRATIONS_PATH}
                                                            component={LazyWrapper(Integrations)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={[UPLOADS_PATH, UPLOADS_VIEWS_PATH]}
                                                            component={LazyWrapper(Uploads)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={BRAND_KITS_PATH}
                                                            component={LazyWrapper(BrandKits)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={CONTENT_PREFERENCES_PATH}
                                                            component={LazyWrapper(ContentPreferencesScreen)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={[BRAND_PLAN_PATH, CAMPAIGNS_PATH, CONTENT_PLAN_PATH]}
                                                            component={LazyWrapper(ContentPlan)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={APPROVALS_PATH}
                                                            component={LazyWrapper(Approvals)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={[BRAND_PLAN_SCHEDULE_PATH, CAMPAIGN_PATH, CONTENT_PLAN_SCHEDULE_PATH]}
                                                            component={LazyWrapper(ContentPlanSchedule)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={VIRTUAL_MARKETER_PLAYGROUND_PATH}
                                                            component={LazyWrapper(VirtualMarketerPlayground)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={BRAIN_PLAYGROUND_PATH}
                                                            component={LazyWrapper(BrainPlayground)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={VISUAL_STYLES_PLAYGROUND_PATH}
                                                            component={LazyWrapper(VisualStylesPlayground)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={BRAND_KIT_PATH}
                                                            component={LazyWrapper(BrandKit)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={EDITOR_SANDBOX_PATH}
                                                            component={LazyWrapper(EditorDemo)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={WORKSPACE_JOIN_REQUEST_PATH}
                                                            component={LazyWrapper(WorkspaceJoinRequest)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={WRITE_PATH}
                                                            component={LazyWrapper(Write)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={ARTICLE_DEBUG_PATH}
                                                            component={LazyWrapper(ArticleDebug)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={MEMBERS_PATH}
                                                            component={LazyWrapper(Members)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={HANDBOOKS_PATH}
                                                            component={LazyWrapper(Handbooks)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={ACADEMY_PATH}
                                                            component={LazyWrapper(Academy)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={WIZARD_PATH}
                                                            component={LazyWrapper(WizardScreen)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={NATIVE_WEBVIEW_BRAND_KIT_PATH}
                                                            component={LazyWrapper(BrandKitsScreen)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={BLAZE_HOME_PATH}
                                                            component={LazyWrapper(Home)}
                                                          />
                                                          <SentryRoute
                                                            exact
                                                            path={DESIGN_PATH}
                                                            component={LazyWrapper(Design)}
                                                          />
                                                          <SentryRoute
                                                            path="*"
                                                            component={LazyWrapper(CustomPathWrapper)}
                                                          />
                                                          <Redirect to={HOME_PATH} />
                                                        </Switch>
                                                      </AppAuthWrapper>
                                                      <SentryRoute
                                                        path="*"
                                                        component={LazyWrapper(CustomPathWrapper)}
                                                      />
                                                      <Redirect to={HOME_PATH} />
                                                    </Switch>
                                                  </WorkspaceDashboard>
                                                </SearchBarPortalProvider>
                                              </ArticleViewProvider>
                                            </VirtualMarketerOnboardingWrapper>
                                          </MaintenanceModeWrapper>
                                        </Switch>
                                      </Switch>
                                      <GlobalModal />
                                    </AlgoliaClientWrapper>
                                  </AbilityProvider>
                                </LocationProvider>
                              </SentryAdditionalInfo>
                            </TrackingPixels>
                          </Switch>
                          <Route path="/" component={URLModal} />
                        </UpdatesProvider>
                      </ModalStack>
                    </ProseInstanceProvider>
                  </WebsocketProvider>
                </AuthChannelProvider>
              </Router>
            </CloudinaryProvider>
            <FeatureFlagLoader />
          </Provider>
          <FeatureFlagAttributes />
          <Viewport />
        </RootStoreProvider>
      </OverlayContainer>
      {showReactQueryDevTools() && <ReactQueryDevtools initialIsOpen={false} position={'bottom-right'} />}
    </QueryClientProvider>
  )
}

export default Sentry.withProfiler(App)
